import { hasPermission, type Permission, readStaffClaims, type StaffClaims } from '@ctrl/db';
import type { Session } from '@supabase/supabase-js';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { supabase } from './supabase.ts';

/**
 * Staff session + claims.
 *
 * Claims shape the UI only. They are read from the token without verification,
 * which is safe precisely because nothing here is a security boundary: every
 * read the SPA performs is independently enforced by RLS in Postgres, and every
 * privileged action is re-checked by the admin API against a Supabase-verified
 * token. Hiding a button the server would refuse anyway is a usability choice.
 */

interface AuthState {
  session: Session | null;
  claims: StaffClaims;
  /** The account being operated on. Persisted so a reload stays in context. */
  activeAccountId: string | null;
  setActiveAccountId: (accountId: string) => void;
  isLoading: boolean;
  can: (permission: Permission) => boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const ACTIVE_ACCOUNT_KEY = 'ctrl:active-account';

/**
 * The last team the user worked in, kept in `user_metadata` so it follows them
 * across sign-outs and devices. localStorage alone forgot it on sign-out —
 * `signOut` clears the key so the next user on a shared register does not
 * inherit it — which meant every login landed on whichever team came first.
 */
const LAST_ACCOUNT_META = 'last_account_id';

function rememberedAccountId(session: Session | null): string | null {
  const value = session?.user.user_metadata?.[LAST_ACCOUNT_META];

  return typeof value === 'string' ? value : null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeAccountId, setActiveAccountIdState] = useState<string | null>(() =>
    localStorage.getItem(ACTIVE_ACCOUNT_KEY),
  );

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setIsLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
    });

    return () => subscription.subscription.unsubscribe();
  }, []);

  const claims = useMemo(() => readStaffClaims(session), [session]);

  /*
   * Land on a usable team rather than an empty screen when none is chosen, or
   * when the stored one is no longer a membership.
   *
   * Only team accounts qualify. The personal account stays in `account_ids`
   * so RLS lets the user reach their own data, but it is not a workspace and
   * is never selected here — a stored or remembered personal id from before
   * this rule is treated like any other stale choice.
   */
  useEffect(() => {
    const teams = claims.teamAccountIds;
    const stored = activeAccountId;
    if (stored && teams.includes(stored)) return;

    // This browser has no valid choice: fall back to the one on the user.
    const remembered = rememberedAccountId(session);
    const next = remembered && teams.includes(remembered) ? remembered : teams[0];

    if (!next) {
      // No team at all. Clear rather than keep pointing at something the
      // token no longer covers, so the gate can show the no-workspace screen.
      if (stored) {
        setActiveAccountIdState(null);
        localStorage.removeItem(ACTIVE_ACCOUNT_KEY);
      }
      return;
    }

    setActiveAccountIdState(next);
    localStorage.setItem(ACTIVE_ACCOUNT_KEY, next);
  }, [claims, activeAccountId, session]);

  // Write the choice back to the user whenever it changes. Fire-and-forget:
  // the console is already on the new account, and a failed write only means
  // the next login defaults the old way.
  useEffect(() => {
    if (!session || !activeAccountId) return;
    if (rememberedAccountId(session) === activeAccountId) return;

    void supabase.auth.updateUser({ data: { [LAST_ACCOUNT_META]: activeAccountId } });
  }, [session, activeAccountId]);

  const value = useMemo<AuthState>(
    () => ({
      session,
      claims,
      activeAccountId,
      isLoading,
      setActiveAccountId: (accountId: string) => {
        setActiveAccountIdState(accountId);
        localStorage.setItem(ACTIVE_ACCOUNT_KEY, accountId);
      },
      can: (permission: Permission) =>
        activeAccountId ? hasPermission(claims, activeAccountId, permission) : false,
      signOut: async () => {
        await supabase.auth.signOut();
        localStorage.removeItem(ACTIVE_ACCOUNT_KEY);
      },
    }),
    [session, claims, activeAccountId, isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
